import React, { useEffect } from 'react';
import { Drawer, Form, Input, InputNumber, Select, Button } from 'antd';
import { useQuery } from '@apollo/client';
import {
  GET_TT_LOCATION_OPTIONS,
  GetTtLocationOptionsResponse,
  TT_LOCATION_TYPE,
} from 'pages/wms/bags/graphql/queries/getTtLocationOptions';
import { SaveTtPresortConfigInput } from '../graphql/mutations/ttPresortConfigMutations';

const SWIFTX = 'SWIFTX';

type Props = {
  visible: boolean;
  onClose: () => void;
  onSubmit: (values: SaveTtPresortConfigInput) => Promise<void> | void;
  initialValues?: SaveTtPresortConfigInput;
  mode: 'create' | 'edit';
};

const TtPresortConfigFormDrawer: React.FC<Props> = ({ visible, onClose, onSubmit, initialValues, mode }) => {
  const [form] = Form.useForm<SaveTtPresortConfigInput>();
  const isEdit = mode === 'edit';
  const carrier = Form.useWatch('carrierCode', form);
  const isSwiftx = carrier === SWIFTX;

  const commonOpts = { fetchPolicy: 'cache-and-network' as const, skip: !visible };
  // 始发地无独立配置数据，与下一站同为 hub 码，复用下一站(type=3)选项
  const { data: nextStopData } = useQuery<GetTtLocationOptionsResponse>(GET_TT_LOCATION_OPTIONS, {
    variables: { locationType: TT_LOCATION_TYPE.NEXT_STOP }, ...commonOpts,
  });
  const { data: carrierData } = useQuery<GetTtLocationOptionsResponse>(GET_TT_LOCATION_OPTIONS, {
    variables: { locationType: TT_LOCATION_TYPE.CARRIER }, ...commonOpts,
  });
  const { data: bizData } = useQuery<GetTtLocationOptionsResponse>(GET_TT_LOCATION_OPTIONS, {
    variables: { locationType: TT_LOCATION_TYPE.TT_BIZ_TYPE }, ...commonOpts,
  });

  const optLabel = (code: string, name: string) => (name && name !== code ? `${code}（${name}）` : code);
  const toOptions = (rows?: GetTtLocationOptionsResponse) =>
    (rows?.getTtLocationOptions ?? []).map(o => ({ value: o.code, label: optLabel(o.code, o.name) }));
  // 尾程商只取顶级承运商（parentId 为空）：type=5 里还含服务子节点(如 USMPM)，不能作为尾程商选项
  const carrierOptions = (carrierData?.getTtLocationOptions ?? [])
    .filter(o => o.parentId == null || o.parentId === '0')
    .map(o => ({ value: o.code, label: optLabel(o.code, o.name) }));

  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (initialValues) form.setFieldsValue(initialValues);
    }
  }, [visible, initialValues, form]);

  const handleFinish = async (values: SaveTtPresortConfigInput) => {
    await onSubmit({
      ...values,
      id: initialValues?.id,
      sortOrder: values.sortOrder ?? 0,
      // 非 SWIFTX 不需要尾程三段码，置空避免脏数据
      mappedSortingCode: values.carrierCode === SWIFTX ? values.mappedSortingCode : null,
    });
  };

  return (
    <Drawer
      open={visible}
      width={560}
      onClose={onClose}
      title={isEdit ? '编辑预分拣配置' : '新建预分拣配置'}
      destroyOnClose
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" onClick={() => form.submit()}>保存</Button>
        </div>
      }
    >
      <Form layout="vertical" form={form} onFinish={handleFinish}>
        <Form.Item label="始发地" name="fromCode" rules={[{ required: true, message: '请选择始发地' }]}>
          <Select showSearch optionFilterProp="label" placeholder="请选择始发地" options={toOptions(nextStopData)} />
        </Form.Item>
        <Form.Item label="下一站" name="nextStopCode" rules={[{ required: true, message: '请选择下一站' }]}>
          <Select showSearch optionFilterProp="label" placeholder="请选择下一站" options={toOptions(nextStopData)} />
        </Form.Item>
        <Form.Item label="尾程商" name="carrierCode" rules={[{ required: true, message: '请选择尾程商' }]}>
          <Select showSearch optionFilterProp="label" placeholder="请选择尾程商" options={carrierOptions} />
        </Form.Item>
        <Form.Item label="业务类型" name="ttBizType" rules={[{ required: true, message: '请选择业务类型' }]}>
          <Select showSearch optionFilterProp="label" placeholder="请选择业务类型（FBT/CBT/UNLIMITED）" options={toOptions(bizData)} />
        </Form.Item>
        <Form.Item label="预分拣码" name="presortCode" rules={[{ required: true, message: '请输入预分拣码' }]}>
          <Input placeholder="路由键，写入 bag.sorting_rule_second（如 DFW）" />
        </Form.Item>
        {isSwiftx && (
          <Form.Item
            label="尾程三段码"
            name="mappedSortingCode"
            rules={[{ required: true, message: 'SWIFTX 尾程必须填尾程三段码' }]}
          >
            <Input placeholder="含区域字母，如 LAX-PHX-NC（仅 SWIFTX 尾程需要）" />
          </Form.Item>
        )}
        <Form.Item label="面单简称" name="labelCode">
          <Input placeholder="目视分拣展示简称（可选，如 EE）" />
        </Form.Item>
        <Form.Item label="排序" name="sortOrder">
          <InputNumber placeholder="排序值（可选）" style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default TtPresortConfigFormDrawer;
